import { GRPCServices } from "../grpc/grpcapi";
import { ProjectService } from "../project/ProjectService";
import { DialogService } from "../core/dialog/GlobalDialogState";
import { ProgressService } from "../core/progress/GlobalProgressState";
import { GalaxyMigrateDeploymentService } from "../galaxymigrate/GmDeploymentService";
import { makeAutoObservable } from "mobx";
import { DeploymentService } from "../deployment/DeploymentService";
import { ServerData } from "../core/data/ServerData";
import {
    GalaxyMigrateDeploymentDetails,
    GalaxyMigrateMigrationSessionBasicInfo,
    GalaxyMigrateMigrationSessionDetails,
    GalaxyMigrateMigrationSessionInfo,
    GalaxyMigrateStorageConfig,
} from "gc-web-proto/galaxycompletepb/apipb/domainpb/galaxymigrate_pb";
import * as gmapipb from "gc-web-proto/galaxycompletepb/apipb/gmapipb/galaxymigrate_api_pb";
import {
    AutoAllocVolumes,
    CancelMigrationSession,
    CloneMigrationSessionSnapshot,
    CutoverMigrationSession,
    DeleteMigrationSession,
    DeleteSnapshots,
    FinalCutoverMigrationSession,
    GetAutoAllocStatusDetails,
    GetAzureManagedDiskPrice,
    GetAzureManagedDiskRecommendation,
    GetEligibleMigrationSessionCutOverChecklists,
    GetMigrationSessionDetails,
    GetMigrationSessionVolQosRollingAvgLog,
    GetMigrationSessionVolQosState,
    GetMigrationVolumeChangesDistributionView,
    IsPreparedForAutoAlloc,
    ListAzureManagedDiskProducts,
    ListMigrationEvents,
    ListMigrationSessions,
    ListSnapshots,
    PrepareForAutoAlloc,
    RestartMigrationSession,
    RevertCutoverMigrationSession,
    StartMigrationSessionCutOverChecklist,
    SuspendMigrationSession,
    SyncMigrationSession,
    UpdateMigrationSession,
} from "gc-web-proto/galaxycompletepb/apipb/gmapipb/galaxymigrate_api_pb";
import { ServerListData } from "../core/data/ListData";
import {
    AllocateDestinationVolumesRequest,
    AllocateDestinationVolumesResponse,
    AutoAlloc,
    AutoAllocStatus,
    GetMigrationSessionVolumeQosStateResponse,
    IsHostReadyForAllocationResponse,
    ListSnapshotsResponse,
    SyncQos,
} from "gc-web-proto/galaxymigratepb/galaxy_migrate_types_pb";
import { Duration } from "google-protobuf/google/protobuf/duration_pb";
import { MTDIDeploymentInfo } from "gc-web-proto/galaxycompletepb/apipb/domainpb/mtdi_pb";
import { IntegrationConfigInfo, IntegrationModule } from "gc-web-proto/galaxycompletepb/apipb/domainpb/integration_pb";
import { getStepsByAction, PostSyncAction, PostSyncActionConfig } from "./workflow/GmMigrationSessionPostSyncActions";
import { Workflow, WorkflowLog, WorkflowStage, WorkflowStatus } from "gc-web-proto/galaxycompletepb/apipb/domainpb/workflow_pb";
import { GetRunLogs, ListWorkflowRuns } from "gc-web-proto/galaxycompletepb/apipb/workflow_api_pb";
import { MobXStepperState } from "../../common/stepper/MobXStepperComponents";
import { Struct } from "google-protobuf/google/protobuf/struct_pb";
import { v4 as uuid } from "uuid";
import { MigrationParametersType } from "./wizard/GmMigrationSessionParametersForm";
import { convertDateObjectToTimestamp } from "../../common/utils/formatter";
import { createMigrationQosSchedule, getListMigrationSessionsSortFilterConfig } from "./CmcMigrationCommon";
import { ChecklistInfo } from "gc-web-proto/galaxycompletepb/apipb/domainpb/checklist_pb";
import { RecommendationPreference } from "gc-web-proto/galaxycompletepb/apipb/domainpb/planner_pb";
import { GetMigrationVolumeChangesDistributionViewResponse } from "gc-web-proto/galaxymigratepb/galaxy_migrate_migration_pb";
import { appI18n } from "../i18n/i18n";

export class GalaxyMigrateMigrationService {
    protected readonly api: GRPCServices;
    private readonly dialogService: DialogService;
    private readonly progressService: ProgressService;
    protected readonly gmDeploymentService: GalaxyMigrateDeploymentService;
    protected readonly deploymentService: DeploymentService;
    protected readonly projectService: ProjectService;

    storageConfig = new ServerData<GalaxyMigrateStorageConfig>().setDataFetcher(this.fetchStorageConfig.bind(this));
    remoteStorageConfig = new ServerData<GalaxyMigrateStorageConfig>().setDataFetcher(this.fetchRemoteStorageConfig.bind(this));
    wizardState: GmMigrationWizardState;
    autoAllocationState: GmMigrationAutoAllocationState;
    currentSessionVolumeState: GmMigrationSessionVolumeState;
    currentSessionEditState: GmMigrationSessionEditState;
    sessions = new ServerListData<ListMigrationSessions.Response, GalaxyMigrateMigrationSessionBasicInfo, ListMigrationSessions.Request>(
        30,
        getListMigrationSessionsSortFilterConfig()
    ).setDataFetcher(this.fetchSessions.bind(this));
    currentSessionWorkflow = new ServerData<Workflow>().setDataFetcher(this.fetchCurrentSessionWorkflow.bind(this));
    currentSessionWorkflowRuns = new ServerListData<ListWorkflowRuns.Response, WorkflowStatus>(10).setDataFetcher(this.fetchWorkflowRuns.bind(this));
    currentWorkflowRunDetails = new ServerData<WorkflowStatus>().setDataFetcher(this.fetchWorkflowRunDetails.bind(this));
    currentWorkflowRunLogs = new ServerListData<GetRunLogs.Response, WorkflowLog>().setDataFetcher(this.fetchWorkflowRunLogs.bind(this));
    currentSessionID: string = null;
    currentSession = new ServerData<GalaxyMigrateMigrationSessionDetails>().setDataFetcher(this.fetchSessionDetails.bind(this));
    currentSessionVolumeSnapshots = new ServerListData<ListSnapshots.Response, ListSnapshotsResponse.Snapshot>().setDataFetcher(
        this.fetchVolumeSnapshots.bind(this)
    );
    currentSessionSelectedSnapshotVolume: GalaxyMigrateMigrationSessionInfo.Volume = null;
    currentSessionEligibleChecklists = new ServerData<ChecklistInfo.Descriptor.AsObject[]>().setDataFetcher(
        this.getEligibleCutOverChecklistsForSession.bind(this)
    );
    currentSessionMigrationEvents = new ServerData<ListMigrationEvents.Response.AsObject>().setDataFetcher(this.listMigrationEvents.bind(this));

    constructor(
        api: GRPCServices,
        dialogService: DialogService,
        progressService: ProgressService,
        gmDeploymentService: GalaxyMigrateDeploymentService,
        deploymentService: DeploymentService,
        projectService: ProjectService
    ) {
        makeAutoObservable(this);

        this.api = api;
        this.dialogService = dialogService;
        this.progressService = progressService;
        this.gmDeploymentService = gmDeploymentService;
        this.deploymentService = deploymentService;
        this.projectService = projectService;

        this.initWizard();
        this.initAutoAllocationWizard();
        this.initVolumeState();
        this.initEditState();
    }

    setCurrentSessionID(sessionID: string) {
        if (sessionID !== this.currentSessionID) {
            this.currentSessionID = sessionID;
            this.currentSession.resetData();
        }
    }

    get currentSessionCutoverChecklistId() {
        return this.currentSession.data?.getCutoverChecklistId();
    }

    initWizard() {
        this.wizardState = new GmMigrationWizardState(this.api, this.gmDeploymentService, this.deploymentService);
        return this.wizardState;
    }

    initAutoAllocationWizard(
        deploymentDetails?: GalaxyMigrateDeploymentDetails,
        sourceDevices?: GmMigrationWizardVolumeState[],
        allowSmallerDestinations?: boolean
    ) {
        if (!!sourceDevices) {
            sourceDevices = sourceDevices.map((d) => {
                d.autoAllocParams = new AutoAlloc.VolumeParams();
                return d;
            });
        }
        this.autoAllocationState = new GmMigrationAutoAllocationState(this.api, this, deploymentDetails, sourceDevices, allowSmallerDestinations);
        return this.autoAllocationState;
    }

    initVolumeState() {
        this.currentSessionVolumeState = new GmMigrationSessionVolumeState(this.api);
        return this.currentSessionVolumeState;
    }

    initEditState() {
        this.currentSessionEditState = new GmMigrationSessionEditState(this.api);
        return this.currentSessionEditState;
    }

    async createSession(req: gmapipb.CreateMigrationSession.Request) {
        return await this.progressService.track(this.api.gmService.createMigrationSession(req, null));
    }

    async fetchSessions(options?: { projectId?: string; operationId?: number }) {
        const projectId = options?.projectId || this.projectService?.currentProjectID;
        const req = new ListMigrationSessions.Request()
            .setPageParams(this.sessions.pagerParam)
            .setProjectId(projectId)
            .setSortParams(this.sessions.sortParam || null)
            .setOperationId(options?.operationId || null);
        this.sessions.addFilterParamsToRequest(req);
        return await this.api.gmService.listMigrationSessions(req, null);
    }

    async fetchSessionDetails(sessionID?: string) {
        sessionID = sessionID || this.currentSessionID;
        const req = new GetMigrationSessionDetails.Request().setSessionId(sessionID);

        const response = await this.api.gmService.getMigrationSessionDetails(req, null);
        return response.getSession();

        //return mockSessionDetails;
    }

    async fetchStorageConfig() {
        const deploymentId = this.currentSession.data?.getSessionInfo().getDeployment().getSystemId();
        //return mockGmStorageConfig;
        return this.gmDeploymentService.fetchStorageConfig(deploymentId, true);
    }

    async fetchRemoteStorageConfig() {
        const deploymentId = this.currentSession.data?.getSessionInfo().getDestinationDeployment().getSystemId();
        return this.gmDeploymentService.fetchStorageConfig(deploymentId, true);
    }

    async fetchCurrentSessionWorkflow(_workflowId: number): Promise<Workflow> {
        throw new Error(appI18n.t("cmcMigration:service.errors.postSyncWorkflowsDeprecated"));
    }

    async fetchWorkflowRuns(_workflowId: number): Promise<ListWorkflowRuns.Response> {
        throw new Error(appI18n.t("cmcMigration:service.errors.postSyncWorkflowsDeprecated"));
    }

    async fetchWorkflowRunDetails(_workflowRunId: number): Promise<WorkflowStatus> {
        throw new Error(appI18n.t("cmcMigration:service.errors.postSyncWorkflowsDeprecated"));
    }

    async fetchWorkflowRunLogs(_statusId: number): Promise<GetRunLogs.Response> {
        throw new Error(appI18n.t("cmcMigration:service.errors.postSyncWorkflowsDeprecated"));
    }

    async cancelWorkflowRun(_id: number) {
        throw new Error(appI18n.t("cmcMigration:service.errors.postSyncWorkflowsDeprecated"));
    }

    async fetchVolumeSnapshots(devicePath?: string, systemId?: string, integrationId?: number) {
        const statusId = uuid();
        const devPath = devicePath || this.currentSessionSelectedSnapshotVolume.getDestination().getDevicePath();
        const sysId =
            systemId ||
            this.currentSession.data?.getSessionInfo().getDestinationDeployment()?.getSystemId() ||
            this.currentSession.data?.getSessionInfo().getDeployment()?.getSystemId();
        if (!integrationId) {
            throw new Error(appI18n.t("cmcMigration:service.errors.integrationRequiredToListSnapshots"));
        }
        const intId = integrationId;
        const req = new ListSnapshots.Request().setDevicePath(devPath).setSystemId(sysId).setIntegrationId(intId).setStatusId(statusId);
        return await this.api.gmService.listSnapshots(req, null);
    }

    setSelectedSnapshotVolume(vol: GalaxyMigrateMigrationSessionInfo.Volume) {
        this.currentSessionSelectedSnapshotVolume = vol;
    }

    async deleteSnapshots(ids: string[], systemId?: string, integrationId?: number) {
        const statusId = uuid();
        if (!integrationId) {
            throw new Error(appI18n.t("cmcMigration:service.errors.integrationRequiredToDeleteSnapshots"));
        }
        const intId = integrationId;
        const sysId = systemId || this.currentSession.data?.getSessionInfo().getDestinationDeployment().getSystemId();

        const req = new DeleteSnapshots.Request().setIntegrationId(intId).setSystemId(sysId).setSnapshotIdsList(ids).setStatusId(statusId);
        return await this.progressService.track(this.api.gmService.deleteSnapshots(req, null));
    }

    async cloneVolumeBackToHost(systemID: string, devicePath: string, snapshotID: string, integrationId?: number) {
        const statusId = uuid();
        if (!integrationId) {
            throw new Error(appI18n.t("cmcMigration:service.errors.integrationRequiredToCloneSnapshots"));
        }
        const req = new CloneMigrationSessionSnapshot.Request()
            .setIntegrationId(integrationId)
            .setSystemId(systemID)
            .setTargetId(systemID)
            .setSnapshotId(snapshotID)
            .setDevicePath(devicePath)
            .setStatusId(statusId);
        return await this.api.gmService.cloneMigrationSessionSnapshot(req, null);
    }

    async getEligibleCutOverChecklistsForSession(sessionId?: string) {
        const req = new GetEligibleMigrationSessionCutOverChecklists.Request().setSessionId(sessionId || this.currentSessionID);
        //return mockChecklistTypes;
        const res = await this.api.gmService.getEligibleMigrationSessionCutOverChecklists(req, null);
        return res.toObject().checklistDescriptorsList;
    }

    async startCutOverChecklist(type: string) {
        const req = new StartMigrationSessionCutOverChecklist.Request().setSessionId(this.currentSessionID).setType(type);
        return await this.progressService.track(this.api.gmService.startMigrationSessionCutOverChecklist(req, null));
    }

    async listMigrationEvents(sessionId: string, maxEvents: number, pageSequenceToken: number, volUuid?: string) {
        const req = new ListMigrationEvents.Request()
            .setSessionId(sessionId)
            .setMaxEvents(maxEvents)
            .setVolUuidFilter(volUuid)
            .setPageSequenceToken(pageSequenceToken);

        const res = await this.api.gmService.listMigrationEvents(req, null);
        return res.toObject();
    }

    async deleteSession(sessionId: string, force = false, alertError = true) {
        const req = new DeleteMigrationSession.Request().setSessionId(sessionId).setForce(force);
        return await this.progressService.track(this.api.gmService.deleteMigrationSession(req, null), null, alertError);
    }

    async cutoverSession(sessionId: string) {
        const req = new CutoverMigrationSession.Request().setSessionId(sessionId);
        return await this.progressService.track(this.api.gmService.cutoverMigrationSession(req, null), appI18n.t("cmcMigration:progress.performingCutover"));
    }

    async revertCutoverSession(sessionId: string) {
        const req = new RevertCutoverMigrationSession.Request().setSessionId(sessionId);
        return await this.progressService.track(this.api.gmService.revertCutoverMigrationSession(req, null));
    }

    async finalCutoverSession(sessionId: string) {
        const req = new FinalCutoverMigrationSession.Request().setSessionId(sessionId);
        return await this.progressService.track(this.api.gmService.finalCutoverMigrationSession(req, null));
    }

    async restartSession(sessionId: string) {
        const req = new RestartMigrationSession.Request().setSessionId(sessionId);
        return await this.progressService.track(this.api.gmService.restartMigrationSession(req, null));
    }

    async syncSession(sessionId: string) {
        const req = new SyncMigrationSession.Request().setSessionId(sessionId);
        return await this.progressService.track(this.api.gmService.syncMigrationSession(req, null));
    }
    async suspendSession(sessionId: string) {
        const req = new SuspendMigrationSession.Request().setSessionId(sessionId);
        return await this.progressService.track(this.api.gmService.suspendMigrationSession(req, null));
    }

    async cancelSession(sessionId: string) {
        const req = new CancelMigrationSession.Request().setSessionId(sessionId);
        return await this.progressService.track(this.api.gmService.cancelMigrationSession(req, null));
    }

    async completeSession(sessionId: string) {
        const req = new CancelMigrationSession.Request().setSessionId(sessionId);
        return await this.progressService.track(this.api.gmService.cancelMigrationSession(req, null));
    }
}

export enum GmMigrationWizardStep {
    SOURCE_DEPLOYMENT_SELECTION,
    MIGRATION_TYPE,
    VOLUMES_SELECTION,
    MIGRATION_PARAMETERS,
}

export enum GmMigrationType {
    LOCAL,
    REMOTE,
    MOCK_MIGRATION,
}

export enum GmMigrationVolumeType {
    DATA,
    BOOT,
    CLUSTER_TRACKING,
}

export const initialMigrationParameters: MigrationParametersType = {
    labels: [],
    description: "",
    autoReSyncInterval: 1440,
    qosLevel: SyncQos.ImpactLevel.RELENTLESS,
    qosStartTime: new Date(),
    qosSchedule: {
        daily: [],
        weekends: [],
        fridays: [],
    },
    remoteBootMigrationPostCutoverScript: "",
    smartIo: false,
};

export class GmMigrationWizardState {
    protected readonly gmDeploymentService: GalaxyMigrateDeploymentService;
    protected readonly deploymentService: DeploymentService;
    protected readonly api: GRPCServices;
    stepperState = new MobXStepperState(0);

    deploymentId: string;
    deployment = new ServerData<GalaxyMigrateDeploymentDetails>().setDataFetcher(this.fetchDeploymentDetails.bind(this));
    remoteDeployment = new ServerData<GalaxyMigrateDeploymentDetails>().setDataFetcher(this.fetchRemoteDeploymentDetails.bind(this));
    storageConfig = new ServerData<GalaxyMigrateStorageConfig>().setDataFetcher(this.fetchStorageConfig.bind(this));
    remoteStorageConfig = new ServerData<GalaxyMigrateStorageConfig>().setDataFetcher(this.fetchRemoteStorageConfig.bind(this));
    selectedRemoteDestinationId: string;
    showLVMSourceSelectionDialog = false;
    selectedVolumes: { [key: string]: GmMigrationWizardVolumeState } = {};
    showBootVolumes: boolean = null;
    allowSmallerDestinations = false;
    migrationVolumeType: GmMigrationVolumeType = null;
    migrationParameters = initialMigrationParameters;
    postSyncActionsState: GmMigrationSessionPostSyncActionsState;
    migrationType: GmMigrationType = null;
    disableCMotion: boolean = null;

    constructor(api: GRPCServices, gmDeploymentService: GalaxyMigrateDeploymentService, deploymentService: DeploymentService) {
        this.api = api;
        this.gmDeploymentService = gmDeploymentService;
        this.deploymentService = deploymentService;
        this.initPostSyncActionsState();
        makeAutoObservable(this);
    }

    initPostSyncActionsState() {
        this.postSyncActionsState = new GmMigrationSessionPostSyncActionsState();
        this.postSyncActionsState.setWizardState(this);
    }

    private async fetchDeploymentDetails() {
        return this.deploymentService.fetchDeploymentDetails(this.deploymentId);
    }

    private async fetchRemoteDeploymentDetails() {
        return this.deploymentService.fetchDeploymentDetails(this.selectedRemoteDestinationId);
    }

    private async fetchStorageConfig() {
        return this.gmDeploymentService.fetchStorageConfig(this.deploymentId, true);
    }

    private async fetchRemoteStorageConfig() {
        return this.gmDeploymentService.fetchStorageConfig(this.selectedRemoteDestinationId, true);
    }

    public async initDeploymentId(deploymentId: string) {
        this.deploymentId = deploymentId;
        await this.deployment.fetchData();
    }

    public async selectDeployment(deploymentId: string) {
        this.deploymentId = deploymentId;
        await this.deployment.fetchData();
    }

    public async selectDisableCMotion(disable: boolean) {
        this.disableCMotion = disable;
    }

    deselectDeployment() {
        this.deploymentId = null;
        this.deployment.resetData();
    }

    selectRemoteDestination(deploymentId: string) {
        this.selectedRemoteDestinationId = deploymentId;
    }

    deselectRemoteDestination() {
        this.selectedRemoteDestinationId = null;
    }

    get isDeploymentWindows() {
        return this.deployment.ready && this.deployment.data?.getInfo()?.getOsType() === MTDIDeploymentInfo.OSType.WINDOWS;
    }

    get destinationStorageConfig() {
        return this.isRemoteMigration ? this.remoteStorageConfig : this.storageConfig;
    }

    get isRemoteMigration() {
        return this.selectedMigrationType === GmMigrationType.REMOTE;
    }

    get selectedDeploymentName() {
        return this.deployment?.data?.getInfo()?.getDeployment()?.getSystemName();
    }

    selectMigrationType(type: GmMigrationType) {
        this.migrationType = type;
    }

    get selectedMigrationType() {
        return this.migrationType;
    }

    get hasSelectedVolumes() {
        return Object.keys(this.selectedVolumes).length > 0;
    }

    get isMockMigration() {
        return this.migrationType === GmMigrationType.MOCK_MIGRATION;
    }

    setShowBootVolumes(show: boolean) {
        this.showBootVolumes = show;
    }

    setAllowSmallerDestinations(allow: boolean) {
        if (!allow) {
            // if not allow, then check and disable all current pair
            Object.values(this.selectedVolumes).forEach((pair) => {
                if (pair.source?.getBlockDevice()?.getCapacity() > pair.destination?.getBlockDevice()?.getCapacity()) {
                    pair.destination = null;
                }
            });
        }
        this.allowSmallerDestinations = !!allow;
    }

    get bootVolumeSelected() {
        return !!Object.values(this.selectedVolumes).find((v) => v.source.getBlockDevice().getBoot());
    }

    selectMigrationVolumeType(type: GmMigrationVolumeType) {
        if (type === GmMigrationVolumeType.BOOT) {
            this.migrationVolumeType = GmMigrationVolumeType.BOOT;
        } else if (type === GmMigrationVolumeType.CLUSTER_TRACKING) {
            this.migrationVolumeType = GmMigrationVolumeType.CLUSTER_TRACKING;
        } else if (type === GmMigrationVolumeType.DATA) {
            this.migrationVolumeType = GmMigrationVolumeType.DATA;
        }
    }

    get selectedMigrationVolumeType() {
        return this.migrationVolumeType;
    }

    addSelectedVolume(source: GalaxyMigrateStorageConfig.Device): void {
        this.selectedVolumes[source.getBlockDevice().getDeviceName()] = {
            source,
        };
    }

    removeSelectedVolume(source: GalaxyMigrateStorageConfig.Device): void {
        delete this.selectedVolumes[source.getBlockDevice().getDeviceName()];
    }

    selectDestinationVolume(sourceDeviceName: string, destinationPathOrLink: string) {
        const pair = this.selectedVolumes[sourceDeviceName];

        if (pair) {
            let destination: GalaxyMigrateStorageConfig.Device = null;
            for (const d of this.getDestinationCandidatesForSourceVolume(pair.source) || []) {
                if (`${d.getBlockDevice().getWindows() ? "" : "/dev/"}${d.getBlockDevice().getDeviceName()}` === destinationPathOrLink) {
                    destination = d;
                    break;
                }
                for (const link of d.getBlockDevice().getDeviceLinksList()) {
                    if (link === destinationPathOrLink) {
                        destination = d;
                        break;
                    }
                }
            }
            if (!destination) {
                throw new Error(appI18n.t("cmcMigration:service.errors.noDestinationCandidates", { destination: destinationPathOrLink }));
            }

            pair.destination = destination;
        } else {
            throw new Error(appI18n.t("cmcMigration:service.errors.noMigrationPair", { source: sourceDeviceName }));
        }
    }

    getDestinationCandidatesForSourceVolume(source: GalaxyMigrateStorageConfig.Device): GalaxyMigrateStorageConfig.Device[] {
        const blockDevice = source.getBlockDevice();

        const storageConfig = this.destinationStorageConfig;
        return storageConfig?.data?.getDevicesList().filter((d) => {
            const migrationSessionUuid = d.getRole()?.getMigrationSessionUuid();
            if (!!migrationSessionUuid) {
                return false;
            }
            if (d.getCanEnlistAsDestination() || d.getRole()?.getRole() === GalaxyMigrateStorageConfig.Device.RoleInfo.Role.DESTINATION) {
                // fixme tmp changes to allow migrate to smaller devices
                if (d.getBlockDevice().getCapacity() >= blockDevice.getCapacity() || !!this.allowSmallerDestinations) {
                    // only if it is not already selected
                    const destinationDeviceName = d.getBlockDevice().getDeviceName();
                    const notSelectedAsDestination = this.selectedDestinationBlockDevNames.indexOf(destinationDeviceName) === -1;
                    const notSelectedAsSource = this.selectedSourceBlockDevNames.indexOf(destinationDeviceName) === -1;
                    if (this.isRemoteMigration) {
                        return notSelectedAsDestination;
                    } else {
                        return notSelectedAsDestination && notSelectedAsSource;
                    }
                }
            }
            return false;
        });
    }

    get selectedDestinationBlockDevNames() {
        return Object.values(this.selectedVolumes)
            .filter((v) => !!v.destination)
            .map((v) => v.destination.getBlockDevice().getDeviceName());
    }

    get selectedSourceBlockDevNames() {
        return Object.keys(this.selectedVolumes);
    }

    get validVolumeSelections() {
        if (!Object.keys(this.selectedVolumes).length) {
            return false;
        } else {
            for (const v of Object.values(this.selectedVolumes)) {
                if (!v.destination || !v.source) {
                    return false;
                }
            }
        }
        return true;
    }

    confirmVolumeSelections() {
        this.postSyncActionsState.setMigrationVolumeNames(this.selectedDestinationBlockDevNames);
    }

    public async confirmDeploymentSelection() {
        await this.deployment.fetchData();
    }

    public async confirmMigrationTypeSelection() {
        if (this.isRemoteMigration) {
            await this.remoteStorageConfig.fetchData();
        }
        this.selectedVolumes = {};
    }

    get hasQosSchedule() {
        return (
            !!this.migrationParameters.qosSchedule.daily.length ||
            !!this.migrationParameters.qosSchedule.fridays.length ||
            !!this.migrationParameters.qosSchedule.weekends.length
        );
    }

    makeCreateSessionRequest() {
        // with parameters already set
        let req = new gmapipb.CreateMigrationSession.Request()
            .setSmallerDestinationOk(this.allowSmallerDestinations)
            .setLabelsList(this.migrationParameters.labels.map((l) => l.name))
            .setSystemId(this.deploymentId)
            .setDescription(this.migrationParameters.description)
            .setDestinationSystemId(this.selectedRemoteDestinationId)
            .setChangedDataTrackingOnly(this.migrationVolumeType === GmMigrationVolumeType.CLUSTER_TRACKING)
            .setRemoteBootMigrationPostCutoverScript(this.migrationParameters.remoteBootMigrationPostCutoverScript)
            .setCmotionDisabled(this.disableCMotion)
            .setSmartIo(!!this.migrationParameters.smartIo)
            .setMockMigration(this.isMockMigration);

        if (!this.isMockMigration) {
            req.setAutoResyncInterval(new Duration().setSeconds((this.migrationParameters.autoReSyncInterval || 0) * 60));
        }

        if (this.hasQosSchedule) {
            req.setQosStartTime(convertDateObjectToTimestamp(this.migrationParameters.qosStartTime))
                .setQosSchedule(createMigrationQosSchedule(this.migrationParameters.qosSchedule))
                .setQosLevel(SyncQos.ImpactLevel.NONE);
        } else if (!this.hasQosSchedule) {
            req.setQosLevel(this.migrationParameters.qosLevel);
        }

        Object.values(this.selectedVolumes).forEach((v) => {
            const volumeParam = new gmapipb.CreateMigrationSession.Request.VolumeParam().setSourceDeviceName(v.source.getBlockDevice().getDeviceName());

            if (v.destination) {
                volumeParam.setDestinationDeviceName(v.destination.getBlockDevice().getDeviceName());
            }

            req.addVols(volumeParam);
        });
        return req;
    }
}

export class GmMigrationSessionVolumeState {
    private readonly api: GRPCServices;

    currentSessionID: string = null;
    currentSessionVolumeUUID: string = null;
    sessionVolumeQoSState = new ServerData<GetMigrationSessionVolumeQosStateResponse>().setDataFetcher(this.fetchMigrationSessionVolQoSState.bind(this));
    sessionVolumeQoSEntries = new ServerData<SyncQos.RollingAverageLogEntry[]>().setDataFetcher(this.fetchMigrationSessionVolQoSRollingAvgLog.bind(this));
    sessionVolumeBitmap = new ServerData<GetMigrationVolumeChangesDistributionViewResponse>().setDataFetcher(
        this.getMigrationVolumeChangesDistributionView.bind(this)
    );

    constructor(api: GRPCServices) {
        this.api = api;
        makeAutoObservable(this);
    }

    initVolumeState(volumeUUID: string, sessionID: string) {
        if (volumeUUID !== this.currentSessionVolumeUUID) {
            this.currentSessionVolumeUUID = volumeUUID;
            this.sessionVolumeQoSEntries.resetData();
            this.sessionVolumeQoSState.resetData();
        }
        if (sessionID !== this.currentSessionID) {
            this.currentSessionID = sessionID;
        }
    }

    async fetchMigrationSessionVolQoSState(sessionId?: string, volUUID?: string) {
        sessionId = sessionId || this.currentSessionID;
        volUUID = volUUID || this.currentSessionVolumeUUID;
        const req = new GetMigrationSessionVolQosState.Request().setSessionId(sessionId).setVolUuid(volUUID);
        const response = await this.api.gmService.getMigrationSessionVolQosState(req, null);
        return response.getQosStateResponse();
    }

    async fetchMigrationSessionVolQoSRollingAvgLog(sessionId?: string, volUUID?: string, maxRecentPoints: number = 300) {
        sessionId = sessionId || this.currentSessionID;
        volUUID = volUUID || this.currentSessionVolumeUUID;
        const req = new GetMigrationSessionVolQosRollingAvgLog.Request().setSessionId(sessionId).setVolUuid(volUUID).setMaxRecentPoints(maxRecentPoints);
        const response = await this.api.gmService.getMigrationSessionVolQosRollingAvgLog(req, null);
        return response.getEntriesList();
    }

    async getMigrationVolumeChangesDistributionView(sessionId?: string, volUuid?: string) {
        const id = sessionId || this.currentSessionID;
        const uuid = volUuid || this.currentSessionVolumeUUID;
        const req = new GetMigrationVolumeChangesDistributionView.Request().setVolUuid(uuid).setSessionId(id).setNumberOfPoints(5000);
        return await this.api.gmService.getMigrationVolumeChangesDistributionView(req, null);
    }
}

export interface GmMigrationWizardVolumeState {
    source: GalaxyMigrateStorageConfig.Device;
    destination?: GalaxyMigrateStorageConfig.Device;
    autoAllocParams?: AutoAlloc.VolumeParams;
    autoAllocVolumeCapacity?: number;
    autoAllocVolumeName?: string;
}

export enum GmAutoAllocationStep {
    SELECT_INTEGRATION,
    PREPARE_HOST,
    ALLOCATE_VOLUMES,
}

export enum GmAutoAllocationIntegration {
    PURE_CBS = "PURE_CBS",
    AZURE = "AZURE",
    DGS = "DGS",
    AWS = "AWS",
}

export enum GmAutoAllocationActionType {
    CONNECT,
    VALIDATE,
    PREPARE,
    ALLOCATE,
}

export class GmMigrationAutoAllocationState {
    private api: GRPCServices;
    protected migrationService: GalaxyMigrateMigrationService;
    deploymentDetails: GalaxyMigrateDeploymentDetails;
    sourceDevices: GmMigrationWizardVolumeState[];
    selectedIntegration: IntegrationConfigInfo.AsObject = null;

    currentStep = GmAutoAllocationStep.SELECT_INTEGRATION;
    currentAction: GmAutoAllocationActionType;
    currentProgress = new ServerData<AutoAllocStatus>().setDataFetcher(this.fetchCurrentProgressStatus.bind(this));
    showProgressDialog = false;
    progressDialogClosePromise: Promise<void> = null;
    progressDialogClosePromiseResolver: () => void = null;
    hostReadiness = new ServerData<IsHostReadyForAllocationResponse>().setDataFetcher(this.fetchHostReadiness.bind(this));
    statusId: string = null;
    azureRecommendation = new ServerData<GetAzureManagedDiskRecommendation.Response.AsObject>().setDataFetcher(this.getAzureRecommendation.bind(this));
    azureProducts = new ServerData<ListAzureManagedDiskProducts.Response.AsObject>().setDataFetcher(this.listAzureProducts.bind(this));
    allowSmallerDestinations = false;

    constructor(
        api: GRPCServices,
        migrationService: GalaxyMigrateMigrationService,
        deploymentDetails?: GalaxyMigrateDeploymentDetails,
        sourceDevices?: GmMigrationWizardVolumeState[],
        allowSmallerDestinations?: boolean
    ) {
        this.api = api;
        this.migrationService = migrationService;
        this.deploymentDetails = deploymentDetails;
        this.sourceDevices = sourceDevices;
        this.statusId = uuid();
        this.allowSmallerDestinations = allowSmallerDestinations;
        makeAutoObservable(this);
    }

    get systemID() {
        return this.deploymentDetails.getInfo().getDeployment().getSystemId();
    }

    getDefaultVolumeName(device: GmMigrationWizardVolumeState): string {
        const deviceName = device.source.getBlockDevice().getDeviceName();
        if (this.selectedIntegration?.module === IntegrationModule.AZURE) {
            return `${this.migrationService.wizardState.selectedDeploymentName}_${deviceName}`;
        }
        return `${this.deploymentDetails.getInfo().getDeployment().getSystemName()}_${deviceName}`;
    }

    setAws(sourceDevice: AutoAlloc.VolumeParams) {
        sourceDevice.setAws(new AutoAlloc.VolumeParams.AWS().setVolumeType(AutoAlloc.VolumeParams.AWS.VolumeType.GP2));
    }

    setAwsVolumeType(sourceDevice: AutoAlloc.VolumeParams.AWS, volumeType: AutoAlloc.VolumeParams.AWS.VolumeType) {
        sourceDevice.setVolumeType(volumeType);
    }

    getAwsVolumeType(sourceDevice: AutoAlloc.VolumeParams.AWS) {
        if (sourceDevice) {
            return sourceDevice.getVolumeType();
        }
        return null;
    }

    setDeployment(deployment: GalaxyMigrateDeploymentDetails) {
        this.deploymentDetails = deployment;
    }

    setNewStatusId(id: string) {
        this.statusId = id;
    }

    clearStatusId() {
        this.statusId = null;
    }

    setShowProgressDialog(show = false) {
        this.currentProgress.resetData();
        if (show) {
            this.progressDialogClosePromise = new Promise<void>((resolve) => {
                this.progressDialogClosePromiseResolver = resolve;
            });
        } else {
            // when closing
            if (this.progressDialogClosePromiseResolver) {
                this.progressDialogClosePromiseResolver();
            }
            this.progressDialogClosePromiseResolver = null;
            this.progressDialogClosePromise = null;
        }
        this.showProgressDialog = show;
    }

    async waitForProgressDialogToClose() {
        if (this.progressDialogClosePromise) {
            await this.progressDialogClosePromise;
        }
    }

    selectIntegration(integration: IntegrationConfigInfo.AsObject) {
        this.selectedIntegration = integration;
        this.selectCurrentStep(GmAutoAllocationStep.PREPARE_HOST);
    }

    selectCurrentStep(step: GmAutoAllocationStep) {
        this.currentStep = step;
    }

    async fetchCurrentProgressStatus(systemID?: string) {
        systemID = systemID || this.systemID;

        const req = new GetAutoAllocStatusDetails.Request().setSystemId(systemID).setStatusId(this.statusId);

        const response = await this.api.gmService.getAutoAllocStatusDetails(req, null);
        return response.getDetailsResponse().getStatus();
    }

    async fetchHostReadiness(systemID?: string) {
        this.setNewStatusId(uuid());
        systemID = systemID || this.systemID;
        this.currentAction = GmAutoAllocationActionType.VALIDATE;
        const req = new IsPreparedForAutoAlloc.Request().setSystemId(systemID).setIntegrationId(this.selectedIntegration.id).setStatusId(this.statusId);
        const response = await this.api.gmService.isPreparedForAutoAlloc(req, null);
        return response.getReadinessResponse();
    }

    proceedFromHostPrepIfReady() {
        if (!!this.hostReadiness?.data?.getReady()) {
            this.selectCurrentStep(GmAutoAllocationStep.ALLOCATE_VOLUMES);
        }
    }

    async autoPrepareHost(systemID?: string) {
        this.setNewStatusId(uuid());
        systemID = systemID || this.systemID;
        this.currentAction = GmAutoAllocationActionType.PREPARE;
        const req = new PrepareForAutoAlloc.Request().setSystemId(systemID).setIntegrationId(this.selectedIntegration.id).setStatusId(this.statusId);
        await this.api.gmService.prepareForAutoAlloc(req, null);
    }

    async autoConfigure() {
        this.setShowProgressDialog(true);
        await this.autoPrepareHost();
        await this.waitForProgressDialogToClose();
        this.selectCurrentStep(GmAutoAllocationStep.ALLOCATE_VOLUMES);
    }

    async manualCheck() {
        this.setShowProgressDialog(true);
        await this.hostReadiness.fetchData();
        await this.waitForProgressDialogToClose();
        this.proceedFromHostPrepIfReady();
    }

    async getAzureRecommendation(
        devicePathsList: string[],
        windowSize: number,
        workloadGrowth: number,
        storageGrowth: number,
        recommendationPreference: RecommendationPreference
    ) {
        const req = new GetAzureManagedDiskRecommendation.Request()
            .setSystemId(this.migrationService.wizardState.deploymentId)
            .setDestinationSystemId(this.systemID)
            .setPreference(recommendationPreference)
            .setWorkloadGrowth(workloadGrowth)
            .setStorageGrowth(storageGrowth)
            .setIntegrationId(this.selectedIntegration.id)
            .setDevicePathsList(this.sourceDevices.map((d) => d.source.getBlockDevice().getDeviceName()))
            .setWindowSize(windowSize);

        const res = await this.api.gmService.getAzureManagedDiskRecommendation(req, null);
        return res.toObject();
    }

    async getAzurePrice(
        capacity: number,
        iops: number,
        throughput: number,
        systemId: string,
        integrationId: number,
        storageType: AutoAlloc.VolumeParams.Azure.StorageType
    ) {
        const req = new GetAzureManagedDiskPrice.Request()
            .setCapacity(capacity)
            .setIops(iops)
            .setThroughput(throughput)
            .setStorageType(storageType)
            .setSystemId(systemId)
            .setIntegrationId(integrationId);

        const res = await this.api.gmService.getAzureManagedDiskPrice(req, null);
        return res.toObject();
    }

    async listAzureProducts() {
        const req = new ListAzureManagedDiskProducts.Request().setSystemId(this.systemID).setIntegrationId(this.selectedIntegration.id);

        const res = await this.api.gmService.listAzureManagedDiskProducts(req, null);
        return res.toObject();
    }

    async allocateVolumes() {
        this.setNewStatusId(uuid());
        this.currentAction = GmAutoAllocationActionType.ALLOCATE;
        const sourceDeployment = this.migrationService.wizardState.selectedDeploymentName;
        const req = new AutoAllocVolumes.Request()
            .setSystemId(this.systemID)
            .setVolumesList(
                this.sourceDevices.map((d) => {
                    const requestVolume = new AllocateDestinationVolumesRequest.Volume()
                        .setUuid(uuid())
                        .setParams(new AutoAlloc.VolumeParams())
                        .setCapacity(d.autoAllocVolumeCapacity || d.source.getBlockDevice().getCapacity())
                        .setName(
                            d.autoAllocVolumeName ||
                                `${this.deploymentDetails.getInfo().getDeployment().getSystemName()}_${d.source.getBlockDevice().getDeviceName()}`
                        );
                    if (this.selectedIntegration.module === IntegrationModule.AZURE) {
                        requestVolume.getParams().setAzure(d.autoAllocParams.getAzure());
                        if (!d.autoAllocVolumeName) {
                            requestVolume.setName(`${sourceDeployment}_${d.source.getBlockDevice().getDeviceName()}`);
                        }
                    } else if (this.selectedIntegration.module === IntegrationModule.PURE) {
                        requestVolume.getParams().setPurecbs(d.autoAllocParams.getPurecbs());
                    } else if (this.selectedIntegration.module === IntegrationModule.DGS) {
                        requestVolume.getParams().setDgs(new AutoAlloc.VolumeParams.DGS());
                    } else if (this.selectedIntegration.module === IntegrationModule.AWS) {
                        requestVolume.getParams().setAws(d.autoAllocParams.getAws());
                    } else if (
                        this.selectedIntegration.module === IntegrationModule.NETAPP ||
                        this.selectedIntegration.module === IntegrationModule.AWS_FSX_NETAPP
                    ) {
                        requestVolume.getParams().setNetapp(d.autoAllocParams.getNetapp());
                    } else if (
                        this.selectedIntegration.module === IntegrationModule.FC_POWERSTORE ||
                        this.selectedIntegration.module === IntegrationModule.POWERSTORE
                    ) {
                        requestVolume.getParams().setPowerstore(d.autoAllocParams.getPowerstore());
                    } else if (
                        this.selectedIntegration.module === IntegrationModule.FC_POWERMAX ||
                        this.selectedIntegration.module === IntegrationModule.POWERMAX
                    ) {
                        requestVolume.getParams().setPowermax(d.autoAllocParams.getPowermax());
                    } else if (this.selectedIntegration.module === IntegrationModule.GCP) {
                        requestVolume.getParams().setGcp(d.autoAllocParams.getGcp());
                    } else if (this.selectedIntegration.module === IntegrationModule.ORACLE) {
                        requestVolume.getParams().setOracle(d.autoAllocParams.getOracle());
                    } else if (this.selectedIntegration.module === IntegrationModule.POWERFLEX) {
                        requestVolume.getParams().setPowerflex(d.autoAllocParams.getPowerflex());
                    } else if (this.selectedIntegration.module === IntegrationModule.AZURESAN) {
                        requestVolume.getParams().setAzuresan(d.autoAllocParams.getAzuresan());
                    } else if (this.selectedIntegration.module === IntegrationModule.PUREAZURE) {
                        requestVolume.getParams().setPureazure(d.autoAllocParams.getPureazure());
                    } else if (this.selectedIntegration.module === IntegrationModule.VMWAREDATASTORE) {
                        requestVolume.getParams().setVmware(d.autoAllocParams.getVmware());
                    } else if (this.selectedIntegration.module === IntegrationModule.NUTANIX) {
                        requestVolume.getParams().setNutanix(d.autoAllocParams.getNutanix());
                    } else if (this.selectedIntegration.module === IntegrationModule.OVIRT) {
                        requestVolume.getParams().setOvirt(d.autoAllocParams.getOvirt());
                    } else if (this.selectedIntegration.module === IntegrationModule.KUBERNETES) {
                        requestVolume.getParams().setKubevirt(d.autoAllocParams.getKubevirt());
                    } else if (this.selectedIntegration.module === IntegrationModule.CLOUDSTACK) {
                        requestVolume.getParams().setCloudstack(d.autoAllocParams.getCloudstack());
                    } else if (this.selectedIntegration.module === IntegrationModule.FC_INFINIBOX) {
                        requestVolume.getParams().setInfinibox(d.autoAllocParams.getInfinibox());
                    } else if (this.selectedIntegration.module === IntegrationModule.PROXMOX) {
                        requestVolume.getParams().setProxmox(d.autoAllocParams.getProxmox());
                    } else if (this.selectedIntegration.module === IntegrationModule.HITACHIVOSB) {
                        requestVolume.getParams().setHitachiVosb(d.autoAllocParams.getHitachiVosb());
                    } else if (this.selectedIntegration.module === IntegrationModule.SILKSDP) {
                        requestVolume.getParams().setSilksdp(d.autoAllocParams.getSilksdp());
                    } else if (this.selectedIntegration.module === IntegrationModule.DIGITALOCEAN) {
                        requestVolume.getParams().setDigitalocean(d.autoAllocParams.getDigitalocean());
                    } else if (this.selectedIntegration.module === IntegrationModule.OPENSTACK) {
                        requestVolume.getParams().setOpenstack(d.autoAllocParams.getOpenstack());
                    } else if (this.selectedIntegration.module === IntegrationModule.FC_PURE) {
                        requestVolume.getParams().setPurecbs(d.autoAllocParams.getPurecbs());
                    }
                    return requestVolume;
                })
            )
            .setIntegrationId(this.selectedIntegration.id)
            .setStatusId(this.statusId);
        const response = await this.api.gmService.autoAllocVolumes(req, null);
        return response.getAllocateResponse().getAllocatedVolumesList();
    }

    async applyAllocatedVolumesToWizard(volumes: AllocateDestinationVolumesResponse.Volume[]) {
        // first rescan
        const wizardState = this.migrationService.wizardState;
        await wizardState.storageConfig.fetchData();
        if (wizardState.isRemoteMigration) {
            await wizardState.remoteStorageConfig.fetchData();
        }

        this.sourceDevices.forEach((pair, i) => {
            wizardState.selectDestinationVolume(pair.source.getBlockDevice().getDeviceName(), volumes[i].getDevicePath());
        });
    }
}

export class GmMigrationSessionEditState {
    private readonly api: GRPCServices;
    currentSessionID: string = null;
    deploymentId: string = null;
    sessionDetails: GalaxyMigrateMigrationSessionDetails;

    migrationParameters = initialMigrationParameters;

    postSyncActionsState: GmMigrationSessionPostSyncActionsState;

    initEditState(sessionID: string, sessionDetails: GalaxyMigrateMigrationSessionDetails) {
        if (sessionID !== this.currentSessionID) {
            this.currentSessionID = sessionID;
        }
        this.sessionDetails = sessionDetails;

        this.initPostSyncActionsState();
    }

    initPostSyncActionsState() {
        this.postSyncActionsState = new GmMigrationSessionPostSyncActionsState();
        this.postSyncActionsState.setEditState(this);
    }

    constructor(api: GRPCServices) {
        this.api = api;
        makeAutoObservable(this);
    }

    get hasQosSchedule() {
        return (
            !!this.migrationParameters.qosSchedule.daily.length ||
            !!this.migrationParameters.qosSchedule.fridays.length ||
            !!this.migrationParameters.qosSchedule.weekends.length
        );
    }

    public async updateMigrationSessionDetails(sessionId?: string) {
        const id = sessionId || this.currentSessionID;
        const req = new UpdateMigrationSession.Request()
            .setLabelsList(this.migrationParameters.labels.map((l) => l.name))
            .setSessionId(id)
            .setAutoResyncInterval(new Duration().setSeconds((this.migrationParameters.autoReSyncInterval || 0) * 60))
            .setDescription(this.migrationParameters.description)
            .setRemoteBootMigrationPostCutoverScript(this.migrationParameters.remoteBootMigrationPostCutoverScript);

        if (!!this.migrationParameters.qosStartTime) {
            req.setQosStartTime(convertDateObjectToTimestamp(this.migrationParameters.qosStartTime));
        }

        if (this.hasQosSchedule) {
            req.setQosSchedule(createMigrationQosSchedule(this.migrationParameters.qosSchedule)).setQosLevel(SyncQos.ImpactLevel.NONE);
        } else if (!this.hasQosSchedule) {
            req.setQosLevel(this.migrationParameters.qosLevel);
        }

        if (this.migrationParameters.smartIo !== undefined) {
            req.setSmartIo(!!this.migrationParameters.smartIo);
        }

        return await this.api.gmService.updateMigrationSession(req, null);
    }
}

export class GmMigrationSessionPostSyncActionsState {
    actions: PostSyncAction[] = [];
    currentPostSyncActionConfig: PostSyncActionConfig;
    currentIntegrationId: number;
    currentProperties: any = {};
    currentSystemId: string;
    timeout: number = 3600;

    stepperState: MobXStepperState;
    migrationWizardState: GmMigrationWizardState;
    migrationVolumeNames: string[] = [];
    migrationSessionEditState: GmMigrationSessionEditState;

    constructor() {
        this.stepperState = new MobXStepperState();
        this.getWorkflowListForRequest.bind(this);
        makeAutoObservable(this);
    }

    setWizardState(state: GmMigrationWizardState) {
        this.migrationWizardState = state;
    }

    setEditState(state: GmMigrationSessionEditState) {
        this.migrationSessionEditState = state;
    }

    setMigrationVolumeNames(vols: string[]) {
        this.migrationVolumeNames = vols;
    }

    resetActionCreation() {
        this.currentPostSyncActionConfig = null;
        this.currentIntegrationId = null;
        this.currentProperties = {};
        this.currentSystemId = null;
        this.stepperState = new MobXStepperState();
        this.timeout = 3600;
    }

    selectAction(a: PostSyncActionConfig, index?: number) {
        this.currentPostSyncActionConfig = a;

        const steps = getStepsByAction(a.action, this, index);
        this.stepperState.setStepConfigs(steps);
        this.stepperState.setTotalSteps(steps.length);

        if (a.action === WorkflowStage.Action.SNAPSHOT) {
            this.initSnapshotProperties();
            this.setSnapshotSystemId();
        }
        if (a.action === WorkflowStage.Action.EXECUTE) {
            this.initExecuteCommandProperties();
            this.setExecuteSystemId(this.destinationSystemId ? "" : this.sourceSystemId);
        }
        this.stepperState.goToNextStep();
    }

    get currentActionType() {
        return this.currentPostSyncActionConfig.action;
    }

    selectIntegration(id: number) {
        this.currentIntegrationId = id;
    }

    initSnapshotProperties() {
        this.currentProperties = {
            namePrefix: "snap",
        };
    }

    initExecuteCommandProperties() {
        this.currentProperties = {
            command: "",
            args: [],
        };
    }

    setSnapshotNamePrefix(name: string) {
        this.currentProperties.namePrefix = name;
    }

    setExecuteCommand(command: string) {
        this.currentProperties.command = command;
    }

    setActionTimeout(timeout: number) {
        this.timeout = timeout;
    }

    createSnapshotPropertiesStruct(a: PostSyncAction) {
        const properties: { volumes: { [key: string]: string } } = {
            volumes: {},
        };
        this.migrationVolumeNames.forEach((v) => {
            properties.volumes[v] = a.properties.namePrefix;
        });
        return properties;
    }

    createExecuteCommandStruct(a: PostSyncAction) {
        const properties: { command: string; args: string[] } = {
            command: a.properties.command,
            args: a.properties.args,
        };
        return properties;
    }

    createLogPropertiesStruct() {
        return {
            message: "Hello World",
        };
    }

    get isConfirmDisabled() {
        if (this.stepperState.isLastStep) {
            if (this.currentPostSyncActionConfig.action === WorkflowStage.Action.SNAPSHOT) {
                return !this.currentProperties.namePrefix;
            }
            if (this.currentPostSyncActionConfig.action === WorkflowStage.Action.EXECUTE) {
                return !(this.currentProperties.command && this.currentSystemId);
            }
            if (this.currentPostSyncActionConfig.action === WorkflowStage.Action.LOG) {
                return !this.stepperState.isLastStep;
            }
        }
        return true;
    }

    get sourceSystemId() {
        if (!!this.migrationWizardState) {
            return this.migrationWizardState.deploymentId;
        } else if (!!this.migrationSessionEditState) {
            return this.migrationSessionEditState.sessionDetails.getSessionInfo().getDeployment().getSystemId();
        }
        return undefined;
    }

    get sourceSystemName() {
        if (!!this.migrationWizardState) {
            return this.migrationWizardState.deployment.data.getInfo().getDeployment().getSystemName();
        } else if (!!this.migrationSessionEditState) {
            return this.migrationSessionEditState.sessionDetails.getSessionInfo().getDeployment().getSystemName();
        }
        return undefined;
    }

    get destinationSystemId() {
        if (!!this.migrationWizardState) {
            return this.migrationWizardState.selectedRemoteDestinationId;
        } else if (!!this.migrationSessionEditState) {
            return this.migrationSessionEditState.sessionDetails.getSessionInfo().getDestinationDeployment()?.getSystemId();
        }
        return undefined;
    }

    get destinationSystemName() {
        if (!!this.migrationWizardState) {
            return this.migrationWizardState.remoteDeployment.data.getInfo().getDeployment().getSystemName();
        } else if (!!this.migrationSessionEditState) {
            return this.migrationSessionEditState.sessionDetails.getSessionInfo().getDestinationDeployment()?.getSystemName();
        }
        return undefined;
    }

    setSnapshotSystemId() {
        this.currentSystemId = this.destinationSystemId ?? this.sourceSystemId;
    }

    setExecuteSystemId(id: string) {
        this.currentSystemId = id;
    }

    confirmAction(editActionIndex?: number) {
        const workflowStage = new WorkflowStage()
            .setAction(this.currentPostSyncActionConfig.action)
            .setIntegrationId(this.currentIntegrationId)
            .setTimeout(new Duration().setSeconds(this.timeout))
            .setSystemId(this.currentSystemId)
            .setPause(new Duration().setSeconds(0));

        const postSyncAction: PostSyncAction = {
            config: this.currentPostSyncActionConfig,
            workflowStage: workflowStage,
            properties: this.currentProperties,
        };

        if (editActionIndex >= 0) {
            this.editAction(postSyncAction, editActionIndex);
        } else {
            this.addAction(postSyncAction);
        }

        this.resetActionCreation();
    }

    setPropertiesByAction(a: PostSyncAction) {
        if (a.config.action === WorkflowStage.Action.SNAPSHOT) {
            a.workflowStage.setProperties(Struct.fromJavaScript(this.createSnapshotPropertiesStruct(a)));
        } else if (a.config.action === WorkflowStage.Action.LOG) {
            a.workflowStage.setProperties(Struct.fromJavaScript(this.createLogPropertiesStruct()));
        } else if (a.config.action === WorkflowStage.Action.EXECUTE) {
            a.workflowStage.setProperties(Struct.fromJavaScript(this.createExecuteCommandStruct(a)));
        }
    }

    getWorkflowListForRequest() {
        return this.actions.map((a) => {
            if (!!a.properties) {
                this.setPropertiesByAction(a);
            }
            return a.workflowStage;
        });
    }

    editAction(a: PostSyncAction, i: number) {
        this.actions[i] = a;
    }

    addAction(a: PostSyncAction) {
        this.actions.push(a);
    }

    moveActionUp(a: PostSyncAction, index: number) {
        const prevAction = this.actions[index - 1];
        this.actions[index] = prevAction;
        this.actions[index - 1] = a;
    }

    moveActionDown(a: PostSyncAction, index: number) {
        const prevAction = this.actions[index + 1];
        this.actions[index] = prevAction;
        this.actions[index + 1] = a;
    }

    removeAction(index: number) {
        this.actions.splice(index, 1);
    }
}
